import type { MasterRef, QuoteStage } from '@/lib/types';
import { Badge, type BadgeTone } from '@/components/ui';
import {
  isExpiringSoon,
  isQuoteExpired,
  isStageAccepted,
  isStageDeclined,
} from '@/lib/quote-utils';
import { normaliseStatusName } from '@/lib/invoice-utils';

/*
 * No `QuoteMoney`, deliberately: quote screens render money with `InvoiceMoney`
 * from `@/components/invoices/InvoiceBits`. A quote and the invoice it becomes
 * are the same figures, and two components would eventually disagree about how
 * a null total looks — which is precisely the case that matters.
 */

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/**
 * Empty-state glyph for quotes — a price tag, not a document.
 *
 * Deliberately not a near-copy of `InvoiceIcon`: an invoice is a bill that has
 * already happened, a quote is an offer that has not.
 */
export function QuoteIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M20.6 12.4 12.4 20.6a2 2 0 0 1-2.83 0l-6.2-6.2A2 2 0 0 1 2.8 13V4.8A2 2 0 0 1 4.8 2.8H13a2 2 0 0 1 1.4.58l6.2 6.2a2 2 0 0 1 0 2.82Z" />
      <path d="M7.6 7.6h.01" />
    </svg>
  );
}

/**
 * The quote-stage names this badge has a colour for, matched EXACTLY.
 *
 * ⚠️ **THE TWO VERDICT TONES HAVE LEFT THIS MAP.** `Accepted` and `Declined`
 * are decided by `isStageAccepted()` / `isStageDeclined()`, which read the
 * `is_accepted` / `is_declined` flags migration 080 put on `f_quote_stages`.
 * What is left here is presentational only — stages that assert nothing about
 * whether the customer answered.
 *
 * That split is the point rather than tidiness. If a verdict name stayed in
 * this map it would still paint a renamed row green from its label alone, which
 * is the exact defect the flags were added to end.
 *
 * ⚠️ `Expired` is here and is NOT a verdict: an expired quote is one nobody
 * answered, which is why the server marks it neither accepted nor declined. Its
 * `warning` tone says "this lapsed", not "this was refused".
 *
 * ⚠️ Matched EXACTLY, after case-folding and whitespace collapse, and NEVER by
 * containment. This doc comment used to RECOMMEND substring matching, and the
 * code did it: `includes('accept')` painted **"Not Accepted"** and **"Pending
 * Acceptance"** the same green as `Accepted` — the two most obvious stages
 * anyone would ever add, both saying the customer had agreed when they had not.
 * Same trap as `stageTone()` in `CompanyDetailPage.tsx`, where "Past Client"
 * contains "client", and the same one `isOverdue()` in `@/lib/invoice-utils`
 * was fixed for ("Unpaid" contains "paid"). **Do not reintroduce it** — the
 * flags remove the need for the guess, they do not make the guess safe.
 */
const NON_VERDICT_STAGE_TONES: ReadonlyMap<string, BadgeTone> = new Map<string, BadgeTone>([
  ['expired', 'warning'],
  ['sent', 'info'],
  // Listed rather than left to the default, to record that Draft was considered.
  ['draft', 'neutral'],
]);

/**
 * A quote's stage.
 *
 * The label is always the server's own name, so the TEXT is never wrong here —
 * only the colour can be. House style is never colour as the only signal, which
 * cuts both ways: the colour is a SECOND signal agreeing with the label, so a
 * colour that disagrees tells the reader the label means something it does not,
 * which is worse than no colour at all.
 *
 * ⚠️ **Pass `stages` wherever the page already has the master list.** A quote's
 * own `stage` is `{id, name}` and carries no flag, so without the list this
 * falls back to matching the two verdict names exactly — correct today, and
 * exactly the guess the flags exist to replace. Where the list is absent the
 * badge is no worse than it was before the flags landed.
 */
export function StageBadge({
  stage,
  stages,
}: {
  stage: MasterRef | null;
  stages?: readonly QuoteStage[];
}) {
  if (!stage) return <span className="text-xs text-fg-subtle">No stage</span>;

  /*
   * Verdict first, and ONLY from the resolvers — so a row the server says is
   * not accepted can never be painted as agreement by its label. Accepted wins
   * a contradictory row on purpose: both flags true is a mis-seeded master, and
   * green-when-confused is the reading somebody questions, whereas a red badge
   * on an agreed quote reads as settled bad news and gets acted on.
   */
  const tone: BadgeTone = isStageAccepted(stage, stages)
    ? 'success'
    : isStageDeclined(stage, stages)
      ? 'danger'
      : /*
         * Not a verdict. An unrecognised name means "newer than this map",
         * never "invalid" — the same rule this codebase applies to an
         * unrecognised Xero refusal code. It falls to `neutral`: no colour,
         * rather than a guessed one.
         */
        (NON_VERDICT_STAGE_TONES.get(normaliseStatusName(stage.name)) ?? 'neutral');

  return (
    <Badge tone={tone} dot>
      {stage.name}
    </Badge>
  );
}

/**
 * How much life the quote has left, if that is currently worth saying.
 *
 * Computed here rather than read off the record: there is no `isExpired` and
 * deliberately never will be, because expiry is measured against the VIEWER's
 * today in the viewer's timezone, and a server-side boolean would go stale in a
 * cached response the first midnight after it was written.
 *
 * Renders nothing in the ordinary case — a badge present on every row is a
 * badge nobody reads when it finally means something.
 */
export function ExpiryBadge({ validTill }: { validTill: string | null }) {
  if (validTill === null) return null;

  // The helpers take the whole record but read only this one field.
  const probe = { validTill };

  if (isQuoteExpired(probe)) return <Badge tone="danger">Expired</Badge>;
  if (isExpiringSoon(probe)) return <Badge tone="warning">Expires soon</Badge>;
  return null;
}
